/**
 * @file CardContainer.js
 * @description Card Container used for the Dashboard components, accepts a child component. The underlying component should be wrapped inside it, as the {children} prop.
 * See DashboardPage.js for an example of this component being used.
 */

import React from 'react';
import Tooltip from '../ui/tooltips/Tooltip';
import { ReactComponent as MoreButtonIcon } from '../../utils/icons/more_button.svg';
import './CardContainer.scss';
import { getText } from '../../i18n';

const CardContainer = ({
    title,
    children,
    onDownloadCsv,
    onViewDrillDown,
    toolTipText,
}) => {
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const optionsMenuRef = React.useRef(null);

    React.useEffect(() => {
        const handleOutsideClick = event => {
            if (!optionsMenuRef.current?.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    const handleToggleMenu = () => {
        setIsMenuOpen(current => !current);
    };

    const handleDownloadCsv = () => {
        if (onDownloadCsv) {
            onDownloadCsv();
        }

        setIsMenuOpen(false);
    };

    const handleViewDrillDown = () => {
        if (onViewDrillDown) {
            onViewDrillDown();
        }

        setIsMenuOpen(false);
    };

    return (
        <div className="dashboard-card">
            {(onDownloadCsv || onViewDrillDown) && (
                <div className="dashboard-card-actions" ref={optionsMenuRef}>
                    <button
                        className="dashboard-card-action-button"
                        onClick={handleToggleMenu}
                        type="button"
                    >
                        <MoreButtonIcon aria-label={getText('msg_dcf0784e2e22')} />
                    </button>
                    {isMenuOpen && (
                        <div className="dashboard-card-action-menu">
                            {onDownloadCsv && (
                                <button
                                    className="dashboard-card-action-menu-item"
                                    onClick={handleDownloadCsv}
                                    type="button"
                                >
                                    {getText('msg_eaa216ad406c')}
                                </button>
                            )}
                            {onViewDrillDown && (
                                <button
                                    className="dashboard-card-action-menu-item"
                                    onClick={handleViewDrillDown}
                                    type="button"
                                >
                                    {getText('msg_6319b15f86fe')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
            <div className="dashboard-card-header">
                <div className="dashboard-card-header-title">{title}</div>
                {toolTipText && (
                    <div className="dashboard-card-tooltip-icon-wrapper">
                        <Tooltip html={toolTipText} placement="top">
                            <i
                                className="bi bi-info-circle"
                                style={{
                                    fontSize: '13px',
                                    color: '#1366ff',
                                }}
                            />
                        </Tooltip>
                    </div>
                )}
            </div>
            <div className="dashboard-card-body">{children}</div>
        </div>
    );
};

export default CardContainer;
